import React, { useState, useEffect } from 'react';
import { Building2, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const CompanySelector = ({ driverId, onCompanySelect }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activating, setActivating] = useState(null);

  useEffect(() => {
    if (driverId) {
      fetchDriverCompanies();
    }
  }, [driverId]);

  const fetchDriverCompanies = async () => {
    try {
      const response = await axiosInstance.get(`/company/driver/${driverId}`);
      if (response.data.success) {
        setCompanies(response.data.data.companies);
        setError(null);
      }
    } catch (err) {
      setError('Şirket listesi yüklenemedi');
      console.error('Error fetching driver companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const activateCompany = async (companyId) => {
    setActivating(companyId);
    try {
      const response = await axiosInstance.post(`/company/driver/${driverId}/activate`, {
        company_id: companyId
      });
      
      if (response.data.success) {
        // Update local state
        setCompanies(companies.map(company => ({
          ...company,
          is_active_company: company.id === companyId
        })));
        
        // Notify parent component
        if (onCompanySelect) {
          const activeCompany = companies.find(c => c.id === companyId);
          onCompanySelect(activeCompany);
        }
      }
    } catch (err) {
      setError('Şirket aktivasyonu başarısız');
      console.error('Error activating company:', err);
    } finally {
      setActivating(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { 
        bg: 'bg-green-100', 
        text: 'text-green-800', 
        label: 'Aktif',
        icon: <CheckCircle size={14} />
      },
      inactive: { 
        bg: 'bg-red-100', 
        text: 'text-red-800', 
        label: 'İnaktif',
        icon: <AlertCircle size={14} />
      },
      pending: { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        label: 'Beklemede',
        icon: <Clock size={14} />
      }
    };
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        <span className="ml-1">{config.label}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 h-20 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={fetchDriverCompanies}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center mb-6">
        <Building2 className="h-6 w-6 text-blue-600 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900">Şirket Seçimi</h2>
      </div>

      {companies.length === 0 ? (
        <div className="text-center py-8">
          <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-2">Henüz hiç şirkete bağlı değilsiniz</p>
          <p className="text-sm text-gray-400">
            Şirket yöneticisi sizi şirkete davet etmelidir
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            Çalışmak istediğiniz şirketi seçin. Online olduğunuzda bu şirket için çalışacaksınız.
          </p>
          
          {companies.map((company) => (
            <div
              key={company.id}
              className={`border rounded-lg p-4 transition-all ${
                company.is_active_company 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-medium text-gray-900 mr-3">
                      {company.name}
                    </h3>
                    {company.is_active_company && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <CheckCircle size={14} className="mr-1" />
                        Aktif Şirket
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    {company.address && (
                      <p className="text-sm text-gray-600">📍 {company.address}</p>
                    )}
                    {company.phone && (
                      <p className="text-sm text-gray-600">📞 {company.phone}</p>
                    )}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-600 mr-2">Durum:</span>
                        {getStatusBadge(company.status)}
                      </div>
                      <div className="text-sm text-gray-600">
                        Komisyon: %{company.commission_rate}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Katılım: {new Date(company.joined_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>

                <div className="ml-4">
                  {company.status === 'active' && !company.is_active_company && (
                    <button
                      onClick={() => activateCompany(company.id)}
                      disabled={activating === company.id}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {activating === company.id ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Aktivasyoing...
                        </div>
                      ) : (
                        'Bu Şirket için Çalış'
                      )}
                    </button>
                  )}
                  
                  {company.status === 'pending' && (
                    <div className="text-center">
                      <Clock size={20} className="mx-auto text-yellow-500 mb-1" />
                      <p className="text-xs text-yellow-600">Onay Bekleniyor</p>
                    </div>
                  )}
                  
                  {company.status === 'inactive' && (
                    <div className="text-center">
                      <AlertCircle size={20} className="mx-auto text-red-500 mb-1" />
                      <p className="text-xs text-red-600">İnaktif</p>
                    </div>
                  )}
                  
                  {company.is_active_company && (
                    <div className="text-center">
                      <CheckCircle size={20} className="mx-auto text-green-500 mb-1" />
                      <p className="text-xs text-green-600">Aktif Şirket</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <Building2 size={20} className="text-blue-500 mr-2 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Şirket Değiştirme
                </p>
                <p className="text-sm text-blue-700">
                  İstediğiniz zaman farklı bir şirketi aktif hale getirebilirsiniz. 
                  Online olduğunuzda aktif şirket için çalışırsınız.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
